import { dispatch, store } from 'redux/store';
import { AssetClassMapper } from './mapper';
import {
  getGetAssetClassesQueryKey,
  useGetAssetClass,
  useGetAssetClasses,
  getAssetClasses,
} from 'api-client';
import { AssetClassEntity } from '@planitgeo/treeplotter-domain/lib/modules/asset-class/asset-class/domain/asset-class.model';
import { saveCurrent } from 'assetClass/infrastructure/assetClass/redux';
import { setAssetsFetch } from 'common/infrastructure/network/network.redux';
import { queryClient as moduleQueryClient } from 'common/infrastructure/remoteState/persistQueryClient';
import { AxiosError } from 'axios';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import FieldRepository from '../field/repository';
import { useMemo, useEffect } from 'react';
import useLocalStorage from 'hooks/useLocalStorage';
import TenantRepository from 'common/infrastructure/tenant/repository';

const AssetClassRepository = {
  useGetAssetClass(assetClassId: string) {
    const { auth } = store.getState();
    const tenantId = TenantRepository.getCurrentTenantPid();

    const useQueryResponse = useGetAssetClass(
      tenantId,
      { assetClassId },
      {
        query: {
          enabled: auth.isAuthenticated && !!auth.user,
        },
      }
    );

    const assetClassEntity = useMemo(() => {
      if (!useQueryResponse?.data) {
        return null;
      }
      return AssetClassMapper.fromDTO(useQueryResponse.data);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(useQueryResponse?.data)]);

    return {
      ...useQueryResponse,
      assetClassEntity,
    };
  },

  useGetAssetClasses(tenantId: string | undefined) {
    const { auth } = store.getState();
    const [, setAssetClassAlias] = useLocalStorage('assetClassAlias', '');

    const useQueryResponse = useGetAssetClasses(
      tenantId || '',
      {
        tenantId: tenantId || '',
      },
      {
        query: {
          enabled: !!tenantId && auth.isAuthenticated && !!auth.user,
        },
      }
    );

    const assetClassEntities = useMemo(() => {
      if (!useQueryResponse?.data || !Array.isArray(useQueryResponse.data)) {
        return [];
      }
      return useQueryResponse.data.map(AssetClassMapper.fromDTO);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(useQueryResponse?.data)]);

    useEffect(() => {
      //assetClasAlias is already being referenced in the forms to display the layer name
      //at some point we lost this getting set to local storage
      //TODO: Set the assetClassAlias per user selection when we have more than one asset class
      if (assetClassEntities.length > 0) {
        setAssetClassAlias(assetClassEntities[0]?.name || '');
      }
    }, [assetClassEntities, setAssetClassAlias]);

    if (useQueryResponse?.failureReason && tenantId) {
      const axiosResponse = useQueryResponse?.failureReason as AxiosError;
      const status = axiosResponse.response?.status;
      if (status === 404) {
        this.refetchAssetClasses(tenantId).catch((err) => {
          loggerService.log(LoggerLevel.ERROR, err);
        });
        return {
          ...useQueryResponse,
          assetClassEntities: [],
        };
      }
    }

    return {
      ...useQueryResponse,
      assetClassEntities,
    };
  },

  getAssetFetchStatus(assetClassId: string) {
    // This doesn't require hooks
    const { network: networkStore } = store.getState();
    const { assetFetch }: { assetFetch: boolean } = networkStore;

    return assetFetch && !!assetClassId;
  },

  useCurrentAssetClass(tenantId: string | undefined) {
    const { assetClassEntities, ...useQueryResponse } =
      AssetClassRepository.useGetAssetClasses(tenantId);

    const { assetClasses: assetClassStore } = store.getState();
    const { currentAssetClassId }: { currentAssetClassId: string } = assetClassStore;

    // Use useEffect to handle side effects
    useEffect(() => {
      if (assetClassEntities.length > 0) {
        const assetClass = assetClassEntities.find(
          (t: AssetClassEntity) => t.id === currentAssetClassId
        );

        // If no currentAssetClassId or no assetClass is found, default to first
        if (!assetClass) {
          this.saveCurrent(assetClassEntities[0]?.id);
        }
      }
    }, [assetClassEntities, currentAssetClassId]);

    if (!assetClassEntities.length) {
      return { assetClass: null, useQueryResponse };
    }

    let assetClass = assetClassEntities.find((t: AssetClassEntity) => t.id === currentAssetClassId);
    if (!assetClass) {
      assetClass = assetClassEntities[0];
    }

    return {
      useQueryResponse,
      assetClass,
    };
  },

  saveCurrent(assetClassId: string) {
    dispatch(saveCurrent(assetClassId));

    const { auth } = store.getState();
    if (auth.isAuthenticated) {
      this.preloadData(assetClassId);
    }
  },

  setAssetsFetch(assetFetch: boolean) {
    // This will tell react-query to either enable or disable the assetclass/assets fetch
    dispatch(setAssetsFetch(assetFetch));
  },

  preloadData(assetClassId: string) {
    FieldRepository.prefetchFields(assetClassId).catch((error) => {
      loggerService.log(LoggerLevel.ERROR, error);
    });
  },

  async prefetchAssetClasses(tenantId: string) {
    const queryKey = getGetAssetClassesQueryKey(tenantId, { tenantId });
    await moduleQueryClient.prefetchQuery({
      queryKey,
      queryFn: () => getAssetClasses(tenantId, { tenantId }),
    });
  },

  async refetchAssetClasses(tenantId: string) {
    if (!tenantId) {
      return;
    }

    const queryKey = getGetAssetClassesQueryKey(tenantId, {
      tenantId,
    });
    await moduleQueryClient.refetchQueries({ queryKey });
  },
};

export default AssetClassRepository;
